import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, UserRound } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import NewsletterForm from "../components/NewsletterForm";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" }) : "";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useSeo({
    lang,
    title: article?.title || t("articles.title"),
    description:
      (article?.excerpt || article?.content || "").replace(/\s+/g, " ").slice(0, 160) ||
      (lang === "en"
        ? "Sports science articles: training, physiology, nutrition and rehab, explained simply."
        : "Άρθρα αθλητικής επιστήμης: προπόνηση, φυσιολογία, διατροφή και αποκατάσταση, απλά."),
    image: article?.cover_image,
  });

  useEffect(() => {
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="max-w-3xl mx-auto px-5 pt-20 pb-24 text-center" data-testid="article-not-found">
        <p className="text-[#475569]">{t("articles.empty")}</p>
        <Link to="/articles" className="btn-blue mt-6 inline-flex">{t("articles.back")}</Link>
      </div>
    );
  }

  if (!article) return <p className="max-w-3xl mx-auto px-5 pt-20 text-[#64748B]">{t("common.loading")}</p>;

  return (
    <>
      <article className="max-w-3xl mx-auto px-5 sm:px-6 pt-12 pb-20" data-testid="article-detail-page">
        <Link data-testid="article-back-link" to="/articles" className="inline-flex items-center gap-2 text-sm font-bold text-[#334155] hover:text-[#1D4ED8] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> {t("articles.back")}
        </Link>
        <h1 className="font-heading font-black text-4xl sm:text-5xl mt-6 text-[#0B1B3A] leading-tight" data-testid="article-title">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-5 mt-5 text-xs font-bold uppercase tracking-[0.18em] text-[#64748B]">
          <span className="inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> {fmtDate(article.published_at || article.created_at)}</span>
          {article.author && <span className="inline-flex items-center gap-2"><UserRound className="w-3.5 h-3.5" /> {article.author}</span>}
        </div>
        {article.cover_image && (
          <img src={article.cover_image} alt={article.title} className="w-full rounded-[24px] border border-[#E2E8F0] mt-8 object-cover max-h-[420px]" />
        )}
        <div className="mt-8 text-base text-[#1E293B] leading-relaxed whitespace-pre-wrap" data-testid="article-content">
          {article.content}
        </div>
        <div className="mt-14">
          <NewsletterForm />
        </div>
      </article>
      <Footer />
    </>
  );
}
